import { DetailedInsight } from '@api/api/insights';
import { RangeHorizon, rangeToResampleMapping } from '@chart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ChartAreaWrapper } from '@terminal/components/chart-area-wrapper/ChartAreaWrapper';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ValidChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { useMemo, useRef } from 'react';

import { ChartArea } from './ChartArea';
import styles from './ChartBody.module.scss';
import { filterByDateRange, getCommonDateRange } from './utils';

const SINGLE_PANE_HEIGHT = 180;
const PANE_PADDING_TOP = 40;

export interface ChartBodyProps {
  assets: {
    tag: string;
    snake: string;
    paneIndex: number;
  }[];
  episodes: DetailedInsight['episodes'];
  horizon: RangeHorizon;
}

export const ChartBody = ({ assets, episodes, horizon }: ChartBodyProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const panesRefLimitExceeded = useRef(false);

  const { colors: themeColors } = useChartTheme();

  const initialChartSettings: ValidChartSettings = {
    assets,
    resample: rangeToResampleMapping[horizon],
    horizon,
    domain: null,
    hidden: undefined,
    episodes,
    paneSettings: {
      0: {
        yAxisType: 'split',
      },
    },
  };

  const {
    chartData,
    assetWithError,
    hasError,
    hasEmpty,
    chartState,
    primaryAsset,
    primaryPane,
    chartPaneActions,
    retryInitialChartLoad,
    changeYAxisSettings,
    removeActiveAsset,
    changeChartLineColor,
    hideActiveAsset,
    changeSeriesType,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: false,
    variants: ['customMulti'],
  });

  const { trimmedChartState, trimmedPrimaryAsset } = useMemo(() => {
    if (!chartData.length || !chartState.chartPanes.length) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    const dateRange = getCommonDateRange(chartData);

    if (!dateRange) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    if (dateRange.start > dateRange.end) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    const trimmedPanes = chartState.chartPanes.map(pane => ({
      ...pane,
      chartAssetData: pane.chartAssetData.map(asset => {
        if (!asset.ts.length) {
          return asset;
        }

        const trimmedTs = filterByDateRange(
          asset.ts,
          dateRange.start,
          dateRange.end
        );
        const trimmedTsByTime = new Map<string, number>();
        trimmedTs.forEach((item, idx) => trimmedTsByTime.set(item.time, idx));

        return {
          ...asset,
          ts: trimmedTs,
          tsByTime: trimmedTsByTime,
          originalTs: filterByDateRange(
            asset.originalTs,
            dateRange.start,
            dateRange.end
          ),
        };
      }),
    }));

    const trimmedPrimary = primaryAsset
      ? (trimmedPanes
          .flatMap(p => p.chartAssetData)
          .find(asset => asset.entity.id === primaryAsset.entity.id) ??
        primaryAsset)
      : undefined;

    return {
      trimmedChartState: {
        ...chartState,
        chartPanes: trimmedPanes,
        domain: [
          dateRange.start.toISOString(),
          dateRange.end.toISOString(),
        ] as [string, string],
      },
      trimmedPrimaryAsset: trimmedPrimary,
    };
  }, [chartData, chartState, primaryAsset]);

  const isLoading = !hasError && !hasEmpty && !chartData[0];

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '180px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!trimmedPrimaryAsset || !primaryPane) {
    return null;
  }

  return (
    <ChartAreaWrapper
      className={styles.chartWrapper}
      rootStyle={{ height: assets.length * SINGLE_PANE_HEIGHT }}
      assetWithError={assetWithError}
      hasError={hasError}
      hasEmpty={hasEmpty}
      colors={themeColors}
      chartData={chartData}
      chartConfig={{
        enableZoom: false,
        hoverVariant: 'simple',
        panePaddingTop: PANE_PADDING_TOP,
        lineWidth: 1,
      }}
      onRetry={retryInitialChartLoad}
      renderChartArea={(options, containerRef) => {
        return (
          <ChartArea
            canvasRef={canvasRef}
            containerRef={containerRef}
            panesRefLimitExceeded={panesRefLimitExceeded}
            options={options}
            chartState={trimmedChartState}
            primaryAsset={trimmedPrimaryAsset}
            primaryPane={primaryPane}
            chartPaneActions={chartPaneActions}
            eventsConfig={{
              TMI: true,
            }}
            onChangeTicker={() => {}}
            onChangeIndicator={() => {}}
            changeYAxisSettings={changeYAxisSettings}
            removeActiveAsset={removeActiveAsset}
            changeChartLineColor={changeChartLineColor}
            hideActiveAsset={hideActiveAsset}
            changeSeriesType={changeSeriesType}
          />
        );
      }}
    />
  );
};
